<template>
  <BaseContainer>
    <div
      class="pt-8 md:py-10 bg-gray-10 px-5 md:px-16 min-[1330px]:-mx-16 rounded-t-3xl space-y-10"
    >
      <div
        class="text-[32px] md:text-5xl text-center md:text-left -mb-3 md:mb-0"
      >
        {{
          locale === 'en'
            ? pageData?.[0]?.headerEn
            : pageData?.[0]?.headerTh || pageData?.[0]?.headerEn
        }}
      </div>
      <nuxt-error-boundary @error="() => {}">
        <Swiper
          :modules="[SwiperAutoplay, SwiperEffectCreative, SwiperPagination]"
          :slides-per-view="1"
          :space-between="24"
          :pagination="{
            clickable: true,
            bulletActiveClass: '!bg-[#626670]',
            renderBullet: (_index, className) => {
              return (
                `<span class='` +
                className +
                ` !w-3 !h-3 !mb-[22px] !bg-gray-30 !opacity-100'></span>`
              )
            }
          }"
          :creative-effect="{
            prev: {
              shadow: false,
              translate: ['-20%', 0, -1]
            },
            next: {
              translate: ['100%', 0, 0]
            }
          }"
          :breakpoints="{
            500: {
              slidesPerView: 1,
              spaceBetween: 16
            },
            720: {
              slidesPerView: 3,
              spaceBetween: 16
            },
            1200: {
              slidesPerView: 4,
              spaceBetween: 16
            }
          }"
        >
          <SwiperSlide
            v-for="(item, index) in pageData"
            :key="index"
            class="pb-[92px] md:pb-0"
          >
            <NuxtLinkLocale
              :key="index"
              prefetch
              class="w-full hover:opacity-50"
              :to="item.href"
            >
              <div class="relative w-full">
                <div
                  class="top-0 left-0 w-full h-[400px] bg-gray-10 rounded-xl"
                >
                  <NuxtImg
                    format="webp"
                    loading="lazy"
                    alt="special collection"
                    :src="item.imageUrl"
                    class="w-full h-full object-cover rounded-xl"
                  />
                  <div
                    :class="
                      twJoin(
                        'absolute bottom-0 right-0 z-10 pt-3 pl-3 bg-gray-10 rounded-tl-xl',
                        `after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-3 after:h-3 after:bg-[radial-gradient(circle_at_100%_100%,_transparent_12px,_#F2F2F3_12px)] after:rotate-180 after:-translate-x-[100%]`,
                        `before:content-[''] before:absolute before:right-0 before:top-0 before:w-3 before:h-3 before:bg-[radial-gradient(circle_at_100%_100%,_transparent_12px,_#F2F2F3_12px)] before:rotate-180 before:-translate-y-[100%]`
                      )
                    "
                  >
                    <div
                      class="w-16 h-16 bg-black flex items-center justify-center rounded-xl"
                    >
                      <svg
                        width="26"
                        height="26"
                        viewBox="0 0 26 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M25.5 2.01562V19.5156C25.5 20.2188 24.875 20.7656 24.25 20.7656C23.5469 20.7656 23 20.2188 23 19.5156V5.0625L2.60938 25.4531C2.375 25.6875 2.0625 25.7656 1.75 25.7656C1.35938 25.7656 1.04688 25.6875 0.8125 25.4531C0.34375 24.9844 0.34375 24.125 0.8125 23.6562L21.2031 3.26562H6.67188C6.04688 3.26562 5.42188 2.64062 5.5 2.01562C5.5 1.3125 6.04688 0.765625 6.75 0.765625H24.25C24.875 0.765625 25.5 1.39062 25.5 2.01562Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-10 relative z-10 pt-4 space-y-2">
                <div class="text-xl font-bold">
                  {{
                    locale === 'en'
                      ? item.titleEn
                      : item.titleTh || item.titleEn
                  }}
                </div>
                <div class="text-gray-50">
                  {{
                    locale === 'en'
                      ? item.descriptionEn
                      : item.descriptionTh || item.descriptionEn
                  }}
                </div>
              </div>
            </NuxtLinkLocale>
          </SwiperSlide>
        </Swiper>
      </nuxt-error-boundary>
    </div>
  </BaseContainer>
</template>

<script lang="ts" setup>
import { twJoin } from 'tailwind-merge'
import { cmsRepository } from '~/repository/cmsRepository'

const { locale } = useI18n()

const { $serverApi } = useNuxtApp()
const cmsRepo = cmsRepository($serverApi)

const { data: pageData } = await useAsyncData<
  {
    headerEn: string
    headerTh: string
    imageUrl: string
    mobileImageUrl?: string
    titleEn: string
    titleTh: string
    descriptionEn: string
    descriptionTh?: string
    href?: string
  }[]
>(
  'getLandingPagePromote',
  async () => {
    const response = await cmsRepo.getWidget('landing-page-promote')
    if (response.data.data.items) return response.data.data.items
    return null
  },
  { server: false }
)
</script>
